import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  ComponentSingleStyleConfig,
  useDisclosure,
  useOutsideClick,
  useStyleConfig,
} from "@chakra-ui/react";
import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import { hasValue, isNullish } from "../../../../lib/util/common-util";
import { CMButtonSearchCondition } from "../CMButtonSearchCondition/CMButtonSearchCondition";
import { CMButtonPopupSearchConditionDatetimeRange } from "../../button-popup";
import {
  compareDatetime,
  datetimeToText,
} from "../../../../lib/util/common-date-util";
import { DatetimeValue, DatetimeValuePartial } from "../../../../lib";

export type CMButtonSearchConditionDatetimeRangeProps = {
  label: string;

  datePlaceholder?: string;
  timePlaceholder?: string;

  value?: {
    start?: DatetimeValue;
    end?: DatetimeValue;
  };
  minDate?: {
    year: number;
    month: number;
    day: number;
  };
  maxDate?: {
    year: number;
    month: number;
    day: number;
  };

  hideClearButton?: boolean;

  /**
   * 開始日時と終了日時の区切り文字として使用する。
   */
  separatorRange: string;
  labelSubmit: string;
  labelClear: string;

  onChange?: (value: { start?: DatetimeValue; end?: DatetimeValue }) => void;
} & ComponentStyleProps;

export const CMButtonSearchConditionDatetimeRange = ({
  label,

  datePlaceholder = "",
  timePlaceholder = "",

  value = {},

  minDate,
  maxDate,

  hideClearButton = false,

  separatorRange,
  labelSubmit,
  labelClear,

  onChange,

  sx,
  ...rest
}: CMButtonSearchConditionDatetimeRangeProps) => {
  const styles = useStyleConfig("CMButtonSearchConditionDatetimeRangeStyles");

  const displayValue = useMemo(() => {
    return datetimeRangeToText(value ?? undefined, separatorRange);
  }, [value, separatorRange]);

  // ポップアップエリアの表示制御に使用する
  const {
    isOpen: isOpenPopup,
    onClose: onClosePopup,
    onToggle: onTogglePopup,
  } = useDisclosure();

  const [startDatetimeInternal, setStartDatetimeInternal] =
    useState<DatetimeValuePartial>(value.start ?? {});
  const [endDatetimeInternal, setEndDatetimeInternal] =
    useState<DatetimeValuePartial>(value.end ?? {});

  const resetAllInternalState = useCallback(
    (
      newValue: {
        start?: DatetimeValue;
        end?: DatetimeValue;
      } = value,
    ) => {
      setStartDatetimeInternal(newValue.start ?? {});
      setEndDatetimeInternal(newValue.end ?? {});
    },
    [value],
  );

  const handleChangeInternalValue = useCallback(
    (value: DatetimeValuePartial, on: "start" | "end") => {
      switch (on) {
        case "start":
          setStartDatetimeInternal(value);
          break;
        case "end":
          setEndDatetimeInternal(value);
          break;
      }
    },
    [],
  );

  const handleSubmit = () => {
    const _startDatetime = partialToDatetimeValue(startDatetimeInternal);
    const _endDatetime = partialToDatetimeValue(endDatetimeInternal);

    // もしstartとendの大小関係が逆であれば、逆転させて返却する。
    const submittedValue: { start?: DatetimeValue; end?: DatetimeValue } =
      hasValue(_startDatetime) &&
      hasValue(_endDatetime) &&
      compareDatetime(_startDatetime, _endDatetime) > 0
        ? { start: _endDatetime, end: _startDatetime }
        : {
            start: _startDatetime,
            end: _endDatetime,
          };

    onClosePopup();
    resetAllInternalState(submittedValue);
    onChange?.(submittedValue);
  };

  const handleClear = useCallback(() => {
    onClosePopup();
    resetAllInternalState({});
    onChange?.({});
  }, [onChange, onClosePopup, resetAllInternalState]);

  const ref = useRef(null);
  useOutsideClick({
    ref: ref,
    handler: () => {
      onClosePopup();
      resetAllInternalState();
    },
  });

  return (
    <CMButtonSearchCondition
      label={label}
      displayValue={displayValue}
      onClick={onTogglePopup}
      isOpenPopup={isOpenPopup}
      sx={{ ...styles, ...sx }}
      {...rest}
      ref={ref}
    >
      <CMButtonPopupSearchConditionDatetimeRange
        datePlaceholder={datePlaceholder}
        timePlaceholder={timePlaceholder}
        minDate={minDate}
        maxDate={maxDate}
        separatorRange={separatorRange}
        labelSubmit={labelSubmit}
        labelClear={labelClear}
        hideClearButton={hideClearButton}
        startDatetimeInternal={startDatetimeInternal}
        endDatetimeInternal={endDatetimeInternal}
        onChangeInternalValue={handleChangeInternalValue}
        onSubmit={handleSubmit}
        onClear={handleClear}
      />
    </CMButtonSearchCondition>
  );
};

export const CMButtonSearchConditionDatetimeRangeStyles: ComponentSingleStyleConfig =
  {
    baseStyle: {},
  };

/**
 * 日時の部分を受け取って、日時またはundefinedを返す。
 * - dateとtime両方あり -> そのまま返す。
 * - dateのみ -> そのdateの00:00を返す。
 * - timeのみ -> undefinedを返す。
 * - 何もなし -> undefinedを返す。
 */
function partialToDatetimeValue({
  date,
  time,
}: DatetimeValuePartial): DatetimeValue | undefined {
  if (hasValue(date) && hasValue(time)) return { date, time };
  if (hasValue(date)) return { date, time: { hour: 0, minute: 0 } };
  return undefined;
}

/**
 * 日時の範囲を受け取って、文字列を返却する。
 * 開始日時と終了日時が設定されている場合、区切り文字で結合した文字列を返却する。
 * 全体が値なし、または開始も終了も値なしであれば、nullを返却する。
 */
function datetimeRangeToText(
  value?: {
    start?: DatetimeValue | null;
    end?: DatetimeValue | null;
  },
  separatorRange?: string,
): string | null {
  if (isNullish(value)) {
    return null;
  }
  if (isNullish(value.start) && isNullish(value.end)) {
    return null;
  }

  const startStr = hasValue(value.start)
    ? datetimeToText(value.start)
    : undefined;

  const endStr = hasValue(value.end) ? datetimeToText(value.end) : undefined;

  if (hasValue(startStr) && hasValue(endStr)) {
    return startStr + separatorRange + endStr;
  } else if (hasValue(startStr)) {
    return startStr + separatorRange;
  } else if (hasValue(endStr)) {
    return separatorRange + endStr;
  } else {
    return null;
  }
}
